import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Switch,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    FormErrorMessage,
    IconButton,
    Flex,
    Text,
    Badge,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import axios from '../api';

const WatchList = () => {
    const [symbols, setSymbols] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isStopLossModalOpen, setIsStopLossModalOpen] = useState(false);
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [stopLossValue, setStopLossValue] = useState('');
    const [formData, setFormData] = useState({
        symbol: '',
        is_active: true,
        friday_only: false,
        stock_stop_loss_percent: '0.3'  // 默认值为 0.3%
    });
    const [formErrors, setFormErrors] = useState({});
    const toast = useToast();

    const fetchSymbols = async () => {
        try {
            const response = await axios.get('/watchlist');
            setSymbols(response.data);
        } catch (error) {
            toast({
                title: '获取数据失败',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchSymbols();
    }, []);

    const validateForm = () => {
        const errors = {};
        if (!formData.symbol.trim()) {
            errors.symbol = '标的代码不能为空';
        }
        const stopLossPercent = parseFloat(formData.stock_stop_loss_percent);
        if (isNaN(stopLossPercent) || stopLossPercent <= 0 || stopLossPercent > 10) {
            errors.stock_stop_loss_percent = '止损百分比必须在 0-10 之间';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            await axios.post('/watchlist', formData);
            toast({
                title: '添加成功',
                status: 'success',
                duration: 2000,
            });
            setIsModalOpen(false);
            setFormData({ symbol: '', is_active: true, friday_only: false, stock_stop_loss_percent: '0.3' });
            fetchSymbols();
        } catch (error) {
            toast({
                title: '添加失败',
                description: error.response?.data?.detail || error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('确定要删除这个标的吗？')) return;

        try {
            await axios.delete(`/watchlist/${id}`);
            toast({
                title: '删除成功',
                status: 'success',
                duration: 2000,
            });
            fetchSymbols();
        } catch (error) {
            toast({
                title: '删除失败',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleStatusChange = async (id, field, currentValue) => {
        try {
            const symbol = symbols.find(s => s.id === id);
            await axios.put(`/watchlist/${id}`, {
                ...symbol,
                [field]: !currentValue
            });
            fetchSymbols();
        } catch (error) {
            toast({
                title: '更新状态失败',
                description: error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleStopLossClick = (symbol) => {
        setSelectedSymbol(symbol);
        setStopLossValue(symbol.stock_stop_loss_percent?.toString() ?? '');
        setIsStopLossModalOpen(true);
    };

    const handleStopLossSubmit = async () => {
        try {
            // 如果输入为空，则设置为 null
            if (stopLossValue === '') {
                await axios.put(`/watchlist/${selectedSymbol.id}`, {
                    ...selectedSymbol,
                    stock_stop_loss_percent: null
                });
                fetchSymbols();
                setIsStopLossModalOpen(false);
                toast({
                    title: '清除止损百分比成功',
                    status: 'success',
                    duration: 2000,
                });
                return;
            }

            const stopLossPercent = parseFloat(stopLossValue);
            if (isNaN(stopLossPercent) || stopLossPercent <= 0 || stopLossPercent > 10) {
                toast({
                    title: '止损百分比无效',
                    description: '止损百分比必须在 0-10 之间',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            await axios.put(`/watchlist/${selectedSymbol.id}`, {
                ...selectedSymbol,
                stock_stop_loss_percent: stopLossPercent.toString()
            });
            fetchSymbols();
            setIsStopLossModalOpen(false);
            toast({
                title: '更新止损百分比成功',
                status: 'success',
                duration: 2000,
            });
        } catch (error) {
            toast({
                title: '更新止损百分比失败',
                description: error.response?.data?.detail || error.message,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('zh-CN');
    };

    const formatPrice = (price) => {
        if (!price) return '-';
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        if (isNaN(numPrice)) return '-';
        return numPrice.toFixed(2);
    };

    return (
        <Box p={4}>
            <Flex justify="space-between" align="center" mb={4}>
                <Box fontSize="xl" fontWeight="bold">监控标的列表</Box>
                <Button
                    leftIcon={<AddIcon />}
                    colorScheme="blue"
                    onClick={() => setIsModalOpen(true)}
                >
                    添加标的
                </Button>
            </Flex>

            <Table variant="striped" size="sm" colorScheme="gray">
                <Thead>
                    <Tr>
                        <Th>标的代码</Th>
                        <Th isNumeric>最高价</Th>
                        <Th isNumeric>最低价</Th>
                        <Th>更新时间</Th>
                        <Th>状态</Th>
                        <Th>周五限定</Th>
                        <Th>止损百分比</Th>
                        <Th>操作</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    {symbols.map((symbol) => (
                        <Tr 
                            key={symbol.id}
                            _hover={{ bg: "blue.100" }}
                        >
                            <Td py={2}>{symbol.symbol}</Td>
                            <Td py={2} isNumeric>{formatPrice(symbol.last_high)}</Td>
                            <Td py={2} isNumeric>{formatPrice(symbol.last_low)}</Td>
                            <Td py={2}>{formatDateTime(symbol.last_updated)}</Td>
                            <Td py={2}>
                                <Switch
                                    size="sm"
                                    isChecked={symbol.is_active}
                                    onChange={() => handleStatusChange(symbol.id, 'is_active', symbol.is_active)}
                                    colorScheme="green"
                                />
                            </Td>
                            <Td py={2}>
                                <Switch
                                    size="sm"
                                    isChecked={symbol.friday_only}
                                    onChange={() => handleStatusChange(symbol.id, 'friday_only', symbol.friday_only)}
                                    colorScheme="purple"
                                />
                            </Td>
                            <Td py={1}>
                                <Button
                                    size="xs"
                                    height="22px"
                                    minW="60px"
                                    variant="outline"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleStopLossClick(symbol);
                                    }}
                                    colorScheme="blue"
                                >
                                    {symbol.stock_stop_loss_percent ? `${symbol.stock_stop_loss_percent}%` : '设置'}
                                </Button>
                            </Td>
                            <Td py={1}>
                                <Button
                                    size="xs"
                                    height="22px"
                                    minW="60px"
                                    variant="outline"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(symbol.id);
                                    }}
                                    colorScheme="red"
                                >
                                    删除
                                </Button>
                            </Td>
                        </Tr>
                    ))}
                </Tbody>
            </Table>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>添加新标的</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl isRequired isInvalid={!!formErrors.symbol} mb={4}>
                            <FormLabel>标的代码</FormLabel>
                            <Input
                                placeholder="输入标的代码"
                                value={formData.symbol}
                                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                            />
                            <FormErrorMessage>{formErrors.symbol}</FormErrorMessage>
                        </FormControl>

                        <FormControl display="flex" alignItems="center" mb={4}>
                            <FormLabel mb="0">启用监控</FormLabel>
                            <Switch
                                isChecked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                colorScheme="green"
                            />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel mb="0">周五限定</FormLabel>
                            <Switch
                                isChecked={formData.friday_only}
                                onChange={(e) => setFormData({ ...formData, friday_only: e.target.checked })}
                                colorScheme="purple"
                            />
                        </FormControl>

                        <FormControl isInvalid={!!formErrors.stock_stop_loss_percent} mt={4}>
                            <FormLabel>止损百分比</FormLabel>
                            <Input
                                type="number"
                                step="0.1"
                                min="0"
                                max="10"
                                value={formData.stock_stop_loss_percent}
                                onChange={(e) => setFormData({ ...formData, stock_stop_loss_percent: e.target.value })}
                                placeholder="输入止损百分比(0-10)"
                            />
                            <FormErrorMessage>{formErrors.stock_stop_loss_percent}</FormErrorMessage>
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
                            取消
                        </Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            添加
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* 止损百分比修改弹窗 */}
            <Modal isOpen={isStopLossModalOpen} onClose={() => setIsStopLossModalOpen(false)}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>修改止损百分比 - {selectedSymbol?.symbol}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel>止损百分比</FormLabel>
                            <Input
                                type="number"
                                step="0.1"
                                min="0"
                                max="10"
                                value={stopLossValue}
                                onChange={(e) => setStopLossValue(e.target.value)}
                                placeholder="输入止损百分比(0-10)"
                            />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={() => setIsStopLossModalOpen(false)}>
                            取消
                        </Button>
                        <Button colorScheme="blue" onClick={handleStopLossSubmit}>
                            保存
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default WatchList;